import React, { useState, useRef } from 'react';
import { Rnd } from 'react-rnd';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { 
  Plus, 
  Type,
  Image as ImageIcon,
  Square,
  Circle,
  Download,
  Trash2,
  Copy,
  Layers,
  ZoomIn,
  ZoomOut,
  Palette,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline,
  LayoutTemplate,
  Sparkles
} from 'lucide-react';
import Navigation from '../components/Navigation';

// Canvas sizes
const CANVAS_SIZES = {
  instagram_square: { name: 'Instagram Square', width: 1080, height: 1080 },
  instagram_story: { name: 'Instagram Story', width: 1080, height: 1920 },
  facebook_post: { name: 'Facebook Post', width: 1200, height: 630 },
  poster_a4: { name: 'Poster A4', width: 2480, height: 3508 },
  flyer: { name: 'Flyer', width: 1080, height: 1350 }
};

// Google Fonts (popular ones)
const GOOGLE_FONTS = [
  'Inter',
  'Roboto',
  'Poppins',
  'Montserrat',
  'Open Sans',
  'Lato',
  'Raleway',
  'Playfair Display',
  'Bebas Neue',
  'Dancing Script'
];

// Demo premade templates
const DEMO_TEMPLATES = {
  restaurant: {
    name: 'Restaurant Special',
    category: 'Restaurant',
    size: 'instagram_square',
    elements: [
      {
        id: 'bg',
        type: 'background',
        color: '#1a1a1a',
        imageUrl: 'https://picsum.photos/seed/restaurant/1080/1080'
      },
      {
        id: 'overlay',
        type: 'shape',
        shape: 'rectangle',
        x: 50,
        y: 700,
        width: 980,
        height: 330,
        color: '#FF6B6B',
        opacity: 0.95,
        borderRadius: 20
      },
      {
        id: 'title',
        type: 'text',
        x: 80,
        y: 750,
        width: 920,
        height: 120,
        text: 'Special Menu',
        fontSize: 72,
        fontFamily: 'Bebas Neue',
        color: '#ffffff',
        fontWeight: 'bold',
        align: 'left'
      },
      {
        id: 'subtitle',
        type: 'text',
        x: 80,
        y: 900,
        width: 920,
        height: 80,
        text: '50% OFF This Weekend',
        fontSize: 36,
        fontFamily: 'Inter',
        color: '#ffffff',
        align: 'left'
      }
    ]
  },
  realEstate: {
    name: 'Real Estate Listing',
    category: 'Real Estate',
    size: 'instagram_square',
    elements: [
      {
        id: 'bg',
        type: 'background',
        color: '#f8f9fa',
        imageUrl: 'https://picsum.photos/seed/house/1080/1080'
      },
      {
        id: 'topbar',
        type: 'shape',
        shape: 'rectangle',
        x: 0,
        y: 0,
        width: 1080,
        height: 150,
        color: '#2C3E50',
        opacity: 0.9
      },
      {
        id: 'title',
        type: 'text',
        x: 60,
        y: 40,
        width: 960,
        height: 80,
        text: 'Dream Home',
        fontSize: 56,
        fontFamily: 'Montserrat',
        color: '#ffffff',
        fontWeight: 'bold',
        align: 'left'
      },
      {
        id: 'price',
        type: 'shape',
        shape: 'rectangle',
        x: 60,
        y: 920,
        width: 300,
        height: 100,
        color: '#3498DB',
        borderRadius: 15
      },
      {
        id: 'pricetext',
        type: 'text',
        x: 80,
        y: 945,
        width: 260,
        height: 60,
        text: '$450,000',
        fontSize: 42,
        fontFamily: 'Poppins',
        color: '#ffffff',
        fontWeight: 'bold',
        align: 'center'
      }
    ]
  },
  fitness: {
    name: 'Fitness Promo',
    category: 'Fitness',
    size: 'instagram_story',
    elements: [
      {
        id: 'bg',
        type: 'background',
        color: '#000000',
        imageUrl: 'https://picsum.photos/seed/fitness/1080/1920'
      },
      {
        id: 'overlay',
        type: 'shape',
        shape: 'rectangle',
        x: 0,
        y: 600,
        width: 1080,
        height: 600,
        color: '#E74C3C',
        opacity: 0.85
      },
      {
        id: 'title',
        type: 'text',
        x: 80,
        y: 700,
        width: 920,
        height: 200,
        text: 'GET FIT',
        fontSize: 96,
        fontFamily: 'Bebas Neue',
        color: '#ffffff',
        fontWeight: 'bold',
        align: 'center'
      },
      {
        id: 'subtitle',
        type: 'text',
        x: 80,
        y: 950,
        width: 920,
        height: 150,
        text: 'Join Today & Save 30%',
        fontSize: 42,
        fontFamily: 'Poppins',
        color: '#ffffff',
        align: 'center'
      }
    ]
  },
  salon: {
    name: 'Beauty Salon',
    category: 'Salon',
    size: 'instagram_square',
    elements: [
      {
        id: 'bg',
        type: 'background',
        color: '#FFF0F5',
        imageUrl: 'https://picsum.photos/seed/beauty/1080/1080'
      },
      {
        id: 'circle1',
        type: 'shape',
        shape: 'circle',
        x: -100,
        y: -100,
        width: 400,
        height: 400,
        color: '#FF69B4',
        opacity: 0.3
      },
      {
        id: 'circle2',
        type: 'shape',
        shape: 'circle',
        x: 780,
        y: 780,
        width: 400,
        height: 400,
        color: '#DDA0DD',
        opacity: 0.3
      },
      {
        id: 'mainbox',
        type: 'shape',
        shape: 'rectangle',
        x: 140,
        y: 400,
        width: 800,
        height: 280,
        color: '#ffffff',
        opacity: 0.95,
        borderRadius: 30
      },
      {
        id: 'title',
        type: 'text',
        x: 180,
        y: 450,
        width: 720,
        height: 100,
        text: 'Beauty Salon',
        fontSize: 64,
        fontFamily: 'Playfair Display',
        color: '#FF1493',
        fontWeight: 'bold',
        align: 'center'
      },
      {
        id: 'subtitle',
        type: 'text',
        x: 180,
        y: 570,
        width: 720,
        height: 80,
        text: 'Book Your Appointment',
        fontSize: 32,
        fontFamily: 'Raleway',
        color: '#696969',
        align: 'center'
      }
    ]
  },
  tech: {
    name: 'Tech Company',
    category: 'Technology',
    size: 'facebook_post',
    elements: [
      {
        id: 'bg',
        type: 'background',
        color: '#0F172A',
        imageUrl: 'https://picsum.photos/seed/tech/1200/630'
      },
      {
        id: 'sidebar',
        type: 'shape',
        shape: 'rectangle',
        x: 0,
        y: 0,
        width: 500,
        height: 630,
        color: '#6366F1',
        opacity: 0.9
      },
      {
        id: 'title',
        type: 'text',
        x: 50,
        y: 200,
        width: 400,
        height: 150,
        text: 'Innovation',
        fontSize: 56,
        fontFamily: 'Poppins',
        color: '#ffffff',
        fontWeight: 'bold',
        align: 'left'
      },
      {
        id: 'subtitle',
        type: 'text',
        x: 50,
        y: 370,
        width: 400,
        height: 100,
        text: 'Tech Solutions for Tomorrow',
        fontSize: 28,
        fontFamily: 'Inter',
        color: '#ffffff',
        align: 'left'
      }
    ]
  }
};

const DigiBrandingEditor = () => {
  const [canvasSize, setCanvasSize] = useState('instagram_square');
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [backgroundImage, setBackgroundImage] = useState('');
  const canvasRef = useRef(null);

  const canvas = CANVAS_SIZES[canvasSize];
  const scale = Math.min(800 / canvas.width, 600 / canvas.height);

  // Add element functions
  const addText = () => {
    const newElement = {
      id: `text-${Date.now()}`,
      type: 'text',
      x: 100,
      y: 100,
      width: 400,
      height: 100,
      text: 'Double-click to edit',
      fontSize: 32,
      fontFamily: 'Inter',
      color: '#000000',
      fontWeight: 'normal',
      align: 'left'
    };
    setElements([...elements, newElement]);
    setSelectedElement(newElement.id);
  };

  const addShape = (shape) => {
    const newElement = {
      id: `shape-${Date.now()}`,
      type: 'shape',
      shape: shape,
      x: 150,
      y: 150,
      width: 300,
      height: shape === 'circle' ? 300 : 200,
      color: '#3498DB',
      opacity: 1,
      borderRadius: 0
    };
    setElements([...elements, newElement]);
    setSelectedElement(newElement.id);
  };

  const updateElement = (id, updates) => {
    setElements(elements.map(el => el.id === id ? { ...el, ...updates } : el));
  };

  const deleteElement = (id) => {
    setElements(elements.filter(el => el.id !== id));
    setSelectedElement(null);
  };

  const duplicateElement = (id) => {
    const element = elements.find(el => el.id === id);
    if (element) {
      const newElement = {
        ...element,
        id: `${element.type}-${Date.now()}`,
        x: element.x + 20,
        y: element.y + 20
      };
      setElements([...elements, newElement]);
    }
  };

  const loadTemplate = (templateKey) => {
    const template = DEMO_TEMPLATES[templateKey];
    if (!template) return;

    setCanvasSize(template.size);
    
    const bgElement = template.elements.find(el => el.type === 'background');
    if (bgElement) {
      setBackgroundColor(bgElement.color || '#ffffff');
      setBackgroundImage(bgElement.imageUrl || '');
    }

    const otherElements = template.elements.filter(el => el.type !== 'background');
    setElements(otherElements);
    toast.success(`${template.name} template loaded!`);
  };

  const exportAsImage = async () => {
    if (!canvasRef.current) return;

    try {
      toast.info('Generating image...');
      
      const canvas = await html2canvas(canvasRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null
      });

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `digi-branding-${Date.now()}.png`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Image downloaded!');
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export image');
    }
  };

  const selectedElementData = elements.find(el => el.id === selectedElement);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Toolbar */}
        <div className="w-20 bg-white border-r border-gray-200 flex flex-col items-center py-6 gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={addText}
            className="w-14 h-14 flex flex-col items-center justify-center"
            title="Add Text"
          >
            <Type className="w-6 h-6" />
            <span className="text-[10px] mt-1">Text</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => addShape('rectangle')}
            className="w-14 h-14 flex flex-col items-center justify-center"
            title="Add Rectangle"
          >
            <Square className="w-6 h-6" />
            <span className="text-[10px] mt-1">Shape</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => addShape('circle')}
            className="w-14 h-14 flex flex-col items-center justify-center"
            title="Add Circle"
          >
            <Circle className="w-6 h-6" />
            <span className="text-[10px] mt-1">Circle</span>
          </Button>

          <div className="flex-1" />

          <Button
            variant="ghost"
            size="sm"
            onClick={exportAsImage}
            className="w-14 h-14 flex flex-col items-center justify-center text-purple-600"
            title="Download"
          >
            <Download className="w-6 h-6" />
            <span className="text-[10px] mt-1">Export</span>
          </Button>
        </div>

        {/* Center Canvas */}
        <div className="flex-1 overflow-auto bg-gray-100 p-8">
          <div className="max-w-7xl mx-auto">
            {/* Canvas Size Selector */}
            <div className="mb-6 flex items-center justify-between">
              <select
                value={canvasSize}
                onChange={(e) => setCanvasSize(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
              >
                {Object.entries(CANVAS_SIZES).map(([key, size]) => (
                  <option key={key} value={key}>
                    {size.name} ({size.width}×{size.height})
                  </option>
                ))}
              </select>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setElements([])}
                >
                  Clear All
                </Button>
              </div>
            </div>

            {/* Canvas */}
            <div className="flex justify-center">
              <div
                ref={canvasRef}
                className="relative bg-white shadow-2xl"
                style={{
                  width: canvas.width,
                  height: canvas.height,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top center',
                  backgroundColor: backgroundColor,
                  backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
                onClick={() => setSelectedElement(null)}
              >
                {/* Elements */}
                {elements.map((element) => (
                  <Rnd
                    key={element.id}
                    position={{ x: element.x, y: element.y }}
                    size={{ width: element.width, height: element.height }}
                    onDragStop={(e, d) => {
                      updateElement(element.id, { x: d.x, y: d.y });
                    }}
                    onResizeStop={(e, direction, ref, delta, position) => {
                      updateElement(element.id, {
                        width: parseInt(ref.style.width),
                        height: parseInt(ref.style.height),
                        ...position
                      });
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedElement(element.id);
                    }}
                    bounds="parent"
                    className={`${selectedElement === element.id ? 'ring-2 ring-purple-600' : ''}`}
                  >
                    {element.type === 'text' ? (
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => updateElement(element.id, { text: e.target.innerText })}
                        className="w-full h-full outline-none cursor-text"
                        style={{
                          fontSize: `${element.fontSize}px`,
                          fontFamily: element.fontFamily,
                          color: element.color,
                          fontWeight: element.fontWeight,
                          textAlign: element.align,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {element.text}
                      </div>
                    ) : element.type === 'shape' ? (
                      <div
                        className="w-full h-full"
                        style={{
                          backgroundColor: element.color,
                          opacity: element.opacity,
                          borderRadius: element.shape === 'circle' ? '50%' : `${element.borderRadius}px`
                        }}
                      />
                    ) : null}
                  </Rnd>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Properties Panel */}
        <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
          <div className="p-6">
            {/* Templates Section */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                <LayoutTemplate className="w-5 h-5" />
                Demo Templates
              </h3>
              <div className="space-y-2">
                {Object.entries(DEMO_TEMPLATES).map(([key, template]) => (
                  <button
                    key={key}
                    onClick={() => loadTemplate(key)}
                    className="w-full text-left p-3 rounded-lg border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all"
                  >
                    <div className="font-semibold text-sm">{template.name}</div>
                    <div className="text-xs text-gray-500">{template.category}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Element Properties */}
            {selectedElementData ? (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold mb-3">Element Properties</h3>

                {selectedElementData.type === 'text' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Font</label>
                      <select
                        value={selectedElementData.fontFamily}
                        onChange={(e) => updateElement(selectedElement, { fontFamily: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      >
                        {GOOGLE_FONTS.map(font => (
                          <option key={font} value={font}>{font}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Size</label>
                      <input
                        type="number"
                        value={selectedElementData.fontSize}
                        onChange={(e) => updateElement(selectedElement, { fontSize: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="12"
                        max="200"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Color</label>
                      <input
                        type="color"
                        value={selectedElementData.color}
                        onChange={(e) => updateElement(selectedElement, { color: e.target.value })}
                        className="w-full h-10 border rounded-lg cursor-pointer"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Weight</label>
                      <select
                        value={selectedElementData.fontWeight}
                        onChange={(e) => updateElement(selectedElement, { fontWeight: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      >
                        <option value="normal">Normal</option>
                        <option value="bold">Bold</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Align</label>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={selectedElementData.align === 'left' ? 'default' : 'outline'}
                          onClick={() => updateElement(selectedElement, { align: 'left' })}
                        >
                          <AlignLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedElementData.align === 'center' ? 'default' : 'outline'}
                          onClick={() => updateElement(selectedElement, { align: 'center' })}
                        >
                          <AlignCenter className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={selectedElementData.align === 'right' ? 'default' : 'outline'}
                          onClick={() => updateElement(selectedElement, { align: 'right' })}
                        >
                          <AlignRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {selectedElementData.type === 'shape' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Color</label>
                      <input
                        type="color"
                        value={selectedElementData.color}
                        onChange={(e) => updateElement(selectedElement, { color: e.target.value })}
                        className="w-full h-10 border rounded-lg cursor-pointer"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Opacity</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={selectedElementData.opacity}
                        onChange={(e) => updateElement(selectedElement, { opacity: parseFloat(e.target.value) })}
                        className="w-full"
                      />
                      <span className="text-xs text-gray-500">{Math.round(selectedElementData.opacity * 100)}%</span>
                    </div>

                    {selectedElementData.shape === 'rectangle' && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Border Radius</label>
                        <input
                          type="number"
                          value={selectedElementData.borderRadius || 0}
                          onChange={(e) => updateElement(selectedElement, { borderRadius: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border rounded-lg"
                          min="0"
                          max="100"
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => duplicateElement(selectedElement)}
                    className="flex-1"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteElement(selectedElement)}
                    className="flex-1 text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-t border-gray-200 pt-6">
                <p className="text-sm text-gray-500 text-center">
                  Select an element to edit its properties
                </p>
              </div>
            )}

            {/* Background */}
            <div className="mt-6 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-bold mb-3">Background</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Color</label>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-full h-10 border rounded-lg cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Image URL</label>
                  <Input
                    value={backgroundImage}
                    onChange={(e) => setBackgroundImage(e.target.value)}
                    placeholder="https://..."
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigiBrandingEditor;
